import { type JSX, useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutHybridDashboard } from 'Components/PageLayout/PageLayoutHybridDashboard';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { RiskCallout } from 'Components/RiskRegister/RiskCallout/RiskCallout';
import { RiskTable } from 'Components/RiskRegister/RiskTable/RiskTable';
import { Text } from 'Components/Text/Text';
import { RISK_REVIEW_LISTING } from 'Config/PathTemplates';
import { RISK_MANAGEMENT_CONTROL_EFFECTIVENESS, RISK_MANAGEMENT_TARGET_RESIDUAL_RISK } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { ResponseModel } from 'Models/ResponseModel';
import { RiskAssessment, RiskHistory } from 'Models/RiskRegister';

import { AssessmentRiskSnapshotModal } from './AssessmentRiskSnapshotModal';
import styles from './ViewRiskAssessment.module.css';

interface UrlParams {
    timestamp: string;
}

export interface ViewRiskAssessmentProps {
    riskRegisterApi: RiskRegisterApi;
    documentApi: DocumentApi;
}

export const ViewRiskAssessment = (props: ViewRiskAssessmentProps): JSX.Element => {
    const { timestamp } = useParams<keyof UrlParams>() as UrlParams;
    const [pageErrorMessage, setPageErrorMessage] = useState<string>();
    const [previousAssessment, setPreviousAssessment] = useState<RiskAssessment>();
    const [overallControlEffectiveness, setOverallControlEffectiveness] = useState<number>();
    const [selectedRiskHistory, setSelectedRiskHistory] = useState<RiskHistory>();
    const cachedData = useCachedData();

    useEffect(() => {
        const getRiskAssessment = async (): Promise<void> => {
            try {
                const riskAssessmentResponse: ResponseModel<RiskAssessment> = await props.riskRegisterApi.getRiskAssessment(timestamp);
                setPreviousAssessment(riskAssessmentResponse.data);
            } catch (error) {
                setPageErrorMessage(error.message);
            }
        };
        getRiskAssessment();
    }, [props.riskRegisterApi, timestamp]);

    useEffect(() => {
        const fetchOverallControlEffectiveness = async (): Promise<void> => {
            try {
                const effectivenessResponse = await props.riskRegisterApi.getOverallControlEffectiveness();
                setOverallControlEffectiveness(effectivenessResponse.data);
            } catch (error) {
                setPageErrorMessage(error.message);
            }
        };

        fetchOverallControlEffectiveness();
    }, [props.riskRegisterApi]);

    if (pageErrorMessage) {
        return <Text>{pageErrorMessage}</Text>;
    }

    if (previousAssessment && overallControlEffectiveness !== undefined) {
        return (
            <>
                {selectedRiskHistory && <AssessmentRiskSnapshotModal assessmentTimestamp={previousAssessment.timestamp} riskHistory={selectedRiskHistory} submittedBy={previousAssessment.submitted_by} hideModal={() => setSelectedRiskHistory(undefined)} />}
                <PageLayoutHybridDashboard
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={generatePath(RISK_REVIEW_LISTING)}>Risk Reviews</BreadcrumbLink>
                            <BreadcrumbText>View Risk Review</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle="View Risk Review"
                    headerDashlets={[
                        {
                            title: 'Risk Review Details',
                            content: (
                                <>
                                    <div className={styles.detailContainer}>
                                        <div className={styles.stat}>
                                            <Text noStyles variant="Header4" color="darkGray">
                                                Review Date
                                            </Text>
                                            <Text noStyles variant="Text1" color="white">
                                                {iso8601ToUsDateShort(previousAssessment.timestamp)}
                                            </Text>
                                        </div>
                                        <div className={styles.stat}>
                                            <Text noStyles variant="Header4" color="darkGray">
                                                Submitted By
                                            </Text>
                                            <Text noStyles variant="Text1" color="white">
                                                {getUserNameFromSubject(previousAssessment.submitted_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}
                                            </Text>
                                        </div>
                                        <div className={styles.stat}>
                                            <Text noStyles variant="Header4" color="darkGray">
                                                Category
                                            </Text>
                                            <Text noStyles variant="Text1" color="white">
                                                {previousAssessment.category.title}
                                            </Text>
                                        </div>
                                    </div>
                                    <div className={styles.assessmentSection}>
                                        <Text variant="Header4" color="darkGray">
                                            Comments
                                        </Text>
                                        <Text variant="Text3" color="white">
                                            {previousAssessment.comments}
                                        </Text>
                                    </div>
                                    <div className={styles.assessmentSection}>
                                        <Text variant="Header4" color="darkGray">
                                            Attached Files
                                        </Text>
                                        {previousAssessment!.documents?.map((document) => {
                                            return <UploadedFileAndState documentApi={props.documentApi} file={document} key={document.file_id} />;
                                        })}
                                    </div>
                                </>
                            ),
                        },
                        {
                            dashlets: [
                                {
                                    title: 'Inherent Risk',
                                    content: <RiskCallout calloutType="total_inherent_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={previousAssessment.risk_histories} />,
                                },
                                {
                                    title: 'Control Effectiveness',
                                    titleTooltip: RISK_MANAGEMENT_CONTROL_EFFECTIVENESS,
                                    content: <RiskCallout calloutType="control_environment_effectiveness" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={previousAssessment.risk_histories} />,
                                },
                                {
                                    title: 'Current Residual Risk',
                                    content: <RiskCallout calloutType="total_current_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={previousAssessment.risk_histories} />,
                                },
                                {
                                    title: 'Target Residual Risk',
                                    titleTooltip: RISK_MANAGEMENT_TARGET_RESIDUAL_RISK,
                                    content: <RiskCallout calloutType="total_target_risk" completeEnvironmentEffectivenessAverage={overallControlEffectiveness} risks={previousAssessment.risk_histories} />,
                                },
                            ],
                        },
                    ]}
                    body={[
                        {
                            title: 'Risk Listing',
                            content: <RiskTable type="assessment risks" riskHistories={previousAssessment.risk_histories} onAssessmentRiskSelected={setSelectedRiskHistory} />,
                        },
                    ]}
                />
            </>
        );
    } else return <Placeholder />;
};
